/**
 * Enum value→label maps, select option arrays, and badge colour maps for the
 * Project module. Mirrors the label-map pattern used in sp-evaluation.model.ts.
 * (Phase 1 covers the project-level enums; more are added per phase.)
 */

export type ProjectType =
  | 'CONSTRUCTION'
  | 'INFRASTRUCTURE'
  | 'ROAD'
  | 'WATER'
  | 'ENERGY'
  | 'ICT'
  | 'SOCIAL'
  | 'HEALTH'
  | 'EDUCATION'
  | 'AGRICULTURE'
  | 'ENVIRONMENT'
  | 'OTHER';

export type ProjectStatus =
  | 'DRAFT'
  | 'PLANNING'
  | 'PROCUREMENT'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'COMPLETED'
  | 'DEFECTS'
  | 'CLOSED'
  | 'CANCELLED';

export type ProjectPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  CONSTRUCTION: 'Construction',
  INFRASTRUCTURE: 'Infrastructure',
  ROAD: 'Road / Transport',
  WATER: 'Water & Sanitation',
  ENERGY: 'Energy',
  ICT: 'ICT / Digital',
  SOCIAL: 'Social Development',
  HEALTH: 'Health',
  EDUCATION: 'Education',
  AGRICULTURE: 'Agriculture',
  ENVIRONMENT: 'Environment',
  OTHER: 'Other',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  DRAFT: 'Draft',
  PLANNING: 'Planning',
  PROCUREMENT: 'Procurement',
  ACTIVE: 'Active / Under Implementation',
  ON_HOLD: 'On Hold',
  COMPLETED: 'Completed',
  DEFECTS: 'Defects Liability Period',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const PROJECT_PRIORITY_LABELS: Record<ProjectPriority, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

/** Tailwind badge classes per project status. */
export const PROJECT_STATUS_CLASS: Record<ProjectStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PLANNING: 'bg-indigo-100 text-indigo-700',
  PROCUREMENT: 'bg-violet-100 text-violet-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  DEFECTS: 'bg-orange-100 text-orange-700',
  CLOSED: 'bg-slate-200 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

/** Tailwind badge classes per priority. */
export const PROJECT_PRIORITY_CLASS: Record<ProjectPriority, string> = {
  CRITICAL: 'bg-red-100 text-red-700',
  HIGH: 'bg-orange-100 text-orange-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW: 'bg-slate-100 text-slate-600',
};

/** Helper to build `{ label, value }[]` option arrays for selects. */
function toOptions<T extends string>(map: Record<T, string>): { label: string; value: T }[] {
  return (Object.keys(map) as T[]).map((value) => ({ label: map[value], value }));
}

export const PROJECT_TYPE_OPTIONS = toOptions(PROJECT_TYPE_LABELS);
export const PROJECT_STATUS_OPTIONS = toOptions(PROJECT_STATUS_LABELS);
export const PROJECT_PRIORITY_OPTIONS = toOptions(PROJECT_PRIORITY_LABELS);

export function projectTypeLabel(value?: ProjectType | string | null): string {
  return value ? (PROJECT_TYPE_LABELS[value as ProjectType] ?? value) : '—';
}
export function projectStatusLabel(value?: ProjectStatus | string | null): string {
  return value ? (PROJECT_STATUS_LABELS[value as ProjectStatus] ?? value) : '—';
}
export function projectPriorityLabel(value?: ProjectPriority | string | null): string {
  return value ? (PROJECT_PRIORITY_LABELS[value as ProjectPriority] ?? value) : '—';
}
export function projectStatusClass(value?: ProjectStatus | string | null): string {
  return (value && PROJECT_STATUS_CLASS[value as ProjectStatus]) || 'bg-slate-100 text-slate-600';
}
export function projectPriorityClass(value?: ProjectPriority | string | null): string {
  return (value && PROJECT_PRIORITY_CLASS[value as ProjectPriority]) || 'bg-slate-100 text-slate-600';
}
